/**
 * Triangle counts for a scene, split into what's currently VISIBLE (what the
 * camera would draw — the toolbar's live number) and the TOTAL including hidden
 * objects (the tooltip). This is the SCENE's geometry, not the GPU's per-frame
 * work: each mesh counts once regardless of how many passes draw it, instances
 * are expanded, and shared geometry is counted per mesh.
 *
 * Constraints (deliberate): the scan reads counts only and holds NOTHING — no
 * cache, no Set, no reference to any geometry — so it can never retain scene
 * objects, and it is cheap enough to run on the toolbar's ~1s sample.
 *
 * Duck-typed (no `three` import) so it stays unit-testable; the `isEditorOnly`
 * predicate is injected (the real one lives in editorOnly.ts).
 */

export interface SceneTriangleCounts {
    /** Triangles the camera would currently draw: invisible subtrees pruned,
     *  instances expanded, BatchedMesh limited to its visible+active instances. */
    visible: number;
    /** Every mesh in the scene, including hidden ones and inactive-free instances. */
    total: number;
    /** IN-SCENE asset counts (placed in the scene graph, incl. hidden; editor helpers
     *  excluded). Distinct from the resource service's LOADED counts. Meshes are mesh
     *  OBJECTS (an InstancedMesh is one); materials/geometries are DEDUPED by identity.
     *  NOTE: textures are intentionally absent — THIS scan only reads material slots
     *  (`.map`, `.normalMap`…), which WebGPU NodeMaterials leave empty (their textures
     *  live in the TSL node graph — reachable via node traversal, as the resource
     *  observer / nodegraph readers do, but not by this quick pass). Use the resource
     *  service's LOADED texture count instead. */
    meshes: number;
    materials: number;
    geometries: number;
    /** Structural scene-graph facts. These describe traversal shape; they do not
     * estimate frame time or imply that non-rendering nodes are removable. */
    nodes: number;
    maxDepth: number;
    averageDepth: number;
    p95Depth: number;
    nonRenderingNodes: number;
    singleChildNonRenderingNodes: number;
    longestSingleChildChain: number;
    /** lights in the scene (hidden ones included — they are still there) */
    lights: number;
    /** how many of those cast shadows. A shadow caster is an EXTRA render pass
     *  over the scene, so this explains frame cost that triangles never will. */
    shadowCastingLights: number;
    /** distinct shadow map resolutions in use, e.g. ["2048x2048"]. The pass cost
     *  scales with this, and it is the cheapest thing in a scene to get wrong. */
    shadowMapSizes?: string[];
    /**
     * Lights by type, and how many of each cast shadows.
     *
     * The TYPE is the cost: a shadow-casting point light renders the scene into a
     * cube — six faces, six passes — where a directional light renders it once.
     * "3 lights, 3 with shadows" hides the difference between 3 and 18 passes.
     */
    lightTypes?: Record<string, { count: number; shadows: number; shadowMapSizes?: string[] }>;
}

/**
 * The scene that holds the content the user actually sees — for picking AND for
 * counting triangles. On a post-processed pipeline the content renders to an
 * OFFSCREEN target while an empty compositor pass renders to the canvas, so the
 * last-rendered scene is empty (the "0 tris" bug). Order: a pickable last-main,
 * then a pickable last-rendered, then the richest pickable scene in the set
 * (content, by child count), then the last-main/rendered as a last resort.
 * `isPickable` is injected (the engine's rule, which excludes empty/effect scenes).
 */
export function pickContentScene<S extends { children: { readonly length: number } }>(
    lastMain: S | null,
    lastRendered: S | null,
    allScenes: Iterable<S>,
    isPickable: (scene: S) => boolean,
): S | null {
    if (lastMain && isPickable(lastMain)) return lastMain;
    if (lastRendered && isPickable(lastRendered)) return lastRendered;
    let best: S | null = null;
    let bestCount = -1;
    for (const scene of allScenes) {
        if (isPickable(scene) && scene.children.length > bestCount) { best = scene; bestCount = scene.children.length; }
    }
    return best ?? lastMain ?? lastRendered;
}

interface GeoLike {
    index?: { count?: number } | null;
    attributes?: { position?: { count?: number } };
}
interface BatchedInstanceInfo { active?: boolean; visible?: boolean; geometryIndex?: number }
interface GeoRange { indexCount?: number; vertexCount?: number }
type MatLike = Record<string, unknown> & { isMaterial?: boolean };
interface ObjLike {
    visible?: boolean;
    children?: ObjLike[];
    isMesh?: boolean;
    isInstancedMesh?: boolean;
    isBatchedMesh?: boolean;
    isPoints?: boolean;
    isLine?: boolean;
    isSprite?: boolean;
    geometry?: GeoLike | null;
    material?: MatLike | MatLike[] | null;
    /** InstancedMesh: number of instances currently rendered. */
    count?: number;
    /** lights: the class name ("PointLight"), whether it casts, and its map size */
    isLight?: boolean;
    type?: string;
    castShadow?: boolean;
    shadow?: { mapSize?: { width?: number; height?: number } };
    /** BatchedMesh internals — stable field names; read transiently, never held. */
    _instanceInfo?: BatchedInstanceInfo[];
    getGeometryRangeAt?: (geometryId: number, target: GeoRange) => GeoRange;
}

type TriCounts = { visible: number; total: number };
const ZERO: TriCounts = { visible: 0, total: 0 };

/** Triangles of ONE geometry: index count (indexed) or vertex count (non-indexed), /3. */
function triCount(geo: GeoLike | null | undefined): number {
    if (!geo) return 0;
    const indices = geo.index?.count;
    if (typeof indices === "number") return Math.floor(indices / 3);
    const verts = geo.attributes?.position?.count;
    return typeof verts === "number" ? Math.floor(verts / 3) : 0;
}

/** BatchedMesh: sum per instance. Only ACTIVE instances exist; VISIBLE ones also
 *  count toward `visible`. Uses the public getGeometryRangeAt for the per-geometry
 *  triangle count (a reused target — no allocation per instance). */
function batchedTriangles(bm: ObjLike): TriCounts {
    const infos = bm._instanceInfo;
    if (!Array.isArray(infos) || typeof bm.getGeometryRangeAt !== "function") {
        // shape not recognised (version drift) — coarse fallback: the merged geometry once
        const t = triCount(bm.geometry);
        return { visible: t, total: t };
    }
    let visible = 0;
    let total = 0;
    const range: GeoRange = {};
    for (let i = 0; i < infos.length; i++) {
        const inst = infos[i];
        if (!inst || inst.active === false) continue;
        bm.getGeometryRangeAt(inst.geometryIndex ?? 0, range);
        const tris = Math.floor((range.indexCount || range.vertexCount || 0) / 3);
        total += tris;
        if (inst.visible !== false) visible += tris;
    }
    return { visible, total };
}

/** Triangles of one mesh, {visible === total} except for BatchedMesh (per-instance). */
function meshTriangles(obj: ObjLike): TriCounts {
    if (obj.isMesh !== true) return ZERO;
    if (obj.isBatchedMesh === true) return batchedTriangles(obj);
    const per = triCount(obj.geometry);
    if (obj.isInstancedMesh === true) {
        const n = per * Math.max(0, obj.count ?? 0);
        return { visible: n, total: n };
    }
    return { visible: per, total: per };
}

/**
 * Triangles across a handful of meshes, counted the same way the scene stats
 * count them — instancing and BatchedMesh included.
 */
export function countMeshTriangles(objects: Iterable<object>): number {
    let total = 0;
    for (const object of objects) total += meshTriangles(object as ObjLike).total;
    return total;
}

/** A mesh's materials as a flat list (single or array, nulls dropped). */
function materialsOf(obj: ObjLike): MatLike[] {
    const m = obj.material;
    return Array.isArray(m) ? m.filter(Boolean) as MatLike[] : m ? [m] : [];
}

/**
 * Scan `root` once, summing triangles. Continues INTO invisible subtrees (so
 * `total` includes hidden geometry) but only adds to `visible` while the
 * effective scene-graph visibility holds. "Visible" is the `.visible` flag, NOT
 * frustum culling (that is per-camera, per-frame, and expensive). Editor-only
 * objects (grid, gizmos, editor camera) are excluded from both counts.
 */
export function countSceneTriangles(root: ObjLike | null | undefined, isEditorOnly: (o: ObjLike) => boolean): SceneTriangleCounts {
    let visible = 0;
    let total = 0;
    let meshes = 0;
    let lights = 0;
    let shadowCastingLights = 0;
    let nodes = 0;
    let maxDepth = 0;
    let depthTotal = 0;
    let nonRenderingNodes = 0;
    let singleChildNonRenderingNodes = 0;
    let longestSingleChildChain = 0;
    const depths = new Map<number, number>();
    const shadowSizes = new Set<string>();
    const lightTypes: Record<string, { count: number; shadows: number; shadowMapSizes?: string[] }> = {};
    // transient dedup sets — released when the scan returns; nothing is retained.
    const seenMat = new Set<object>();
    const seenGeo = new Set<object>();

    const scan = (obj: ObjLike, parentVisible: boolean, depth: number, parentChain: number): void => {
        if (isEditorOnly(obj)) return; // editor furniture is not "the scene"
        nodes++;
        maxDepth = Math.max(maxDepth, depth);
        depthTotal += depth;
        depths.set(depth, (depths.get(depth) ?? 0) + 1);
        const kids = obj.children;
        let includedChildren = 0;
        if (kids) for (let i = 0; i < kids.length; i++) if (!isEditorOnly(kids[i])) includedChildren++;
        const renders = obj.isMesh === true || obj.isPoints === true || obj.isLine === true || obj.isSprite === true || obj.isLight === true;
        if (!renders) nonRenderingNodes++;
        const single = !renders && includedChildren === 1;
        if (single) singleChildNonRenderingNodes++;
        const chain = single ? parentChain + 1 : 0;
        longestSingleChildChain = Math.max(longestSingleChildChain, chain);
        const vis = parentVisible && obj.visible !== false;
        if (obj.isMesh === true) {
            const t = meshTriangles(obj);
            total += t.total;
            if (vis) visible += t.visible;
            meshes++;
            if (obj.geometry) seenGeo.add(obj.geometry);
            for (const mat of materialsOf(obj)) seenMat.add(mat);
        }
        if (obj.isLight === true) {
            lights++;
            const type = typeof obj.type === "string" && obj.type ? obj.type : "Light";
            const bucket = lightTypes[type] ?? (lightTypes[type] = { count: 0, shadows: 0 });
            bucket.count++;
            if (obj.castShadow === true) bucket.shadows++;
            if (obj.castShadow === true) {
                shadowCastingLights++;
                const map = obj.shadow?.mapSize;
                if (typeof map?.width === "number" && typeof map?.height === "number") {
                    const size = `${map.width}x${map.height}`;
                    shadowSizes.add(size);
                    // ALSO per type: the cost is per light, and one global row hides
                    // that a point light at 1024 pays six faces where a directional
                    // light at 4096 pays one
                    const sizes = bucket.shadowMapSizes ?? (bucket.shadowMapSizes = []);
                    if (!sizes.includes(size)) sizes.push(size);
                }
            }
        }
        if (kids) for (let i = 0; i < kids.length; i++) scan(kids[i], vis, depth + 1, chain);
    };

    if (root) scan(root, root.visible !== false, 0, 0);
    let p95Depth = 0;
    if (nodes > 0) {
        const threshold = Math.ceil(nodes * 0.95);
        let accumulated = 0;
        for (let depth = 0; depth <= maxDepth; depth++) {
            accumulated += depths.get(depth) ?? 0;
            if (accumulated >= threshold) { p95Depth = depth; break; }
        }
    }
    return {
        visible, total, meshes, materials: seenMat.size, geometries: seenGeo.size,
        nodes, maxDepth, averageDepth: nodes ? Math.round((depthTotal / nodes) * 10) / 10 : 0, p95Depth,
        nonRenderingNodes, singleChildNonRenderingNodes, longestSingleChildChain,
        lights, shadowCastingLights,
        shadowMapSizes: shadowSizes.size ? [...shadowSizes] : undefined,
        lightTypes: lights > 0 ? lightTypes : undefined,
    };
}
